import {ReactElement, ReactNode, useId} from 'react';
import {
    Control,
    ControllerFieldState,
    ControllerRenderProps,
    FieldValues,
    Path,
    RegisterOptions,
    UseFormStateReturn,
} from 'react-hook-form';
import {Controller} from 'react-hook-form';

import {FieldContext} from '../context';

type OnRenderCallbackType<T extends FieldValues = FieldValues> = ({
    field,
    fieldState,
    formState,
}: {
    field: ControllerRenderProps<T, Path<T>>;
    fieldState: ControllerFieldState;
    formState: UseFormStateReturn<T>;
}) => ReactElement;

type FieldProps<T extends FieldValues = FieldValues> = {
    name: Path<T>;
    id?: string;
    errorAriaId?: string;
    warningMessage?: string;
    children: ReactNode | OnRenderCallbackType<T>;
    control?: Control<T> | undefined;
    rules?: Omit<RegisterOptions<T, Path<T>>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'> | undefined;
};

export const Field = <T extends FieldValues = FieldValues>({
    name,
    children,
    warningMessage,
    id,
    errorAriaId,
    control,
    rules,
}: FieldProps<T>) => {
    const fallbackId = useId();
    const fallbackErrorAriaId = `${fallbackId}-error`;

    if (!control && typeof children === 'function') {
        throw new Error('Field: control prop is required when children is a validation render function.');
    }

    return (
        <FieldContext.Provider
            value={{
                name,
                warningMessage,
                id: id || fallbackId,
                warningMessageId: errorAriaId || fallbackErrorAriaId,
            }}
        >
            {typeof children === 'function' ? (
                <Controller
                    name={name}
                    control={control}
                    rules={rules}
                    render={({field, fieldState, formState}) => {
                        return children({field, fieldState, formState});
                    }}
                />
            ) : (
                children
            )}
        </FieldContext.Provider>
    );
};
